import { clampMaterialOpacity } from '@/animation/clampMaterialOpacity'
import type { AnimationClip } from '@/types/animationClip'
import type { AnimationLayer } from '@/types/animationLayer'

/**
 * 深拷贝动画片段（含关键帧）。
 *
 * @param c - 源片段
 */
export function deepCloneClip(c: AnimationClip): AnimationClip {
  return {
    id: c.id,
    startSec: c.startSec,
    durationSec: c.durationSec,
    easingPreset: c.easingPreset,
    keyframes: c.keyframes.map((k) => ({
      id: k.id,
      timeInClip: k.timeInClip,
      position: { ...k.position },
      rotationDeg: { ...k.rotationDeg },
      scale: { ...k.scale },
      materialOpacity: clampMaterialOpacity(k.materialOpacity),
      screenImageId: k.screenImageId,
    })),
  }
}

/**
 * 深拷贝图层（含 clips；`screenTextureSrc` 字符串为浅拷贝引用同一 blob URL）。
 *
 * @param l - 源图层
 */
/**
 * 判断两图层在「编辑/撤销」意义上是否等价（用于手势结束去抖）。
 *
 * @param a - 图层 a
 * @param b - 图层 b
 */
export function layerEditStateEquals(a: AnimationLayer, b: AnimationLayer): boolean {
  const pick = (x: AnimationLayer) => ({
    name: x.name,
    locked: x.locked,
    deviceTemplateId: x.deviceTemplateId,
    position: x.position,
    rotationDeg: x.rotationDeg,
    scale: x.scale,
    screenMaterialFileName: x.screenMaterialFileName,
    screenTextureSrc: x.screenTextureSrc,
    screenMediaType: x.screenMediaType,
    screenVideoTimelineOffsetSec: x.screenVideoTimelineOffsetSec,
    screenImageTransitionMode: x.screenImageTransitionMode,
    screenImages: x.screenImages,
    visibilityStartTimeSec: x.visibilityStartTimeSec,
    visibilityEndTimeSec: x.visibilityEndTimeSec,
    visible: x.visible,
    visibilityRuntime: x.visibilityRuntime,
    textContent: x.textContent,
    textFontSize: x.textFontSize,
    textColor: x.textColor,
    textFontWeight: x.textFontWeight,
    textAlign: x.textAlign,
    clips: x.clips,
  })
  return JSON.stringify(pick(a)) === JSON.stringify(pick(b))
}

export function deepCloneLayer(l: AnimationLayer): AnimationLayer {
  return {
    id: l.id,
    name: l.name,
    locked: l.locked,
    deviceTemplateId: l.deviceTemplateId,
    position: { ...l.position },
    rotationDeg: { ...l.rotationDeg },
    scale: { ...l.scale },
    screenMaterialFileName: l.screenMaterialFileName,
    screenTextureSrc: l.screenTextureSrc,
    screenMediaType: l.screenMediaType,
    screenVideoTimelineOffsetSec: l.screenVideoTimelineOffsetSec,
    screenImageTransitionMode: l.screenImageTransitionMode,
    screenImages: l.screenImages.map((it) => ({
      id: it.id,
      fileName: it.fileName,
      textureSrc: it.textureSrc,
    })),
    visibilityStartTimeSec: l.visibilityStartTimeSec,
    visibilityEndTimeSec: l.visibilityEndTimeSec,
    visible: l.visible,
    visibilityRuntime: l.visibilityRuntime
      ? {
          fadeInSec: l.visibilityRuntime.fadeInSec,
          fadeOutSec: l.visibilityRuntime.fadeOutSec,
        }
      : undefined,
    textContent: l.textContent,
    textFontSize: l.textFontSize,
    textColor: l.textColor,
    textFontWeight: l.textFontWeight,
    textAlign: l.textAlign,
    clips: l.clips.map(deepCloneClip),
  }
}
